import React, { Component, ReactNode } from 'react';
import { locale } from '../../common/localization/localizationService';
import { faTruck } from '@fortawesome/free-solid-svg-icons';
import PaneHeadline from '../shared/paneHeadline';
import { IContractor, ICustomer, IUser, IVehicle } from '../../models';
import VehiclesGrid from './VehiclesGrid';
import './Vehicles.scss';
import VehicleEditDialog from './VehicleEditDialog';
import ContractorSelector from '../userAdmin/ContractorSelector';
import { History } from 'history';
import { isNil } from 'lodash';
import { filterVehicles } from './vehicleUtils';
import { IVehiclesState as IVehiclesListState } from '../../redux/modules/vehicles/vehiclesState';
import { getCustomerIdInUse, hasRole } from '../../utils/GeneralUtils';
import { UserRoles } from '../../redux/modules/auth';
import { Spinner2 } from '../shared/spinner2';
export interface IVehiclesProps {
  impersonatedUser: any;
  impersonatedCustomerId: any;
  customers: Array<ICustomer>;
  authentication: IUser;
  history: History;
  vehicles: IVehiclesListState;
  getContractors: (
    customerId: number,
    ignoreCache?: boolean
  ) => Promise<Record<string, any>>;
  getCustomers: () => void;
  fetchVehicles: (ignoreCache?: boolean) => Promise<void>;
  isLoading: boolean;
}

export interface IVehiclesState {
  selectedContractorId: number;
  selectedCustomerId: number;
  contractorsList: Array<IContractor>;
  vehicles: Array<IVehicle>;
  isEditEnabled: boolean;
  editDialog: {
    show: boolean;
    vehicle: IVehicle;
  };
}
export class Vehicles extends Component<IVehiclesProps, IVehiclesState> {
  public readonly state: IVehiclesState = {
    selectedContractorId: this.props.authentication.contractorId ?? -1,
    selectedCustomerId: getCustomerIdInUse(this.props.authentication),
    vehicles: [],
    isEditEnabled: hasRole(this.props.authentication.auth0Roles, [
      UserRoles.SUPERADMINISTRATOR,
    ]),
    editDialog: {
      show: false,
      vehicle: null,
    },
  } as IVehiclesState;

  public componentDidMount(): void {
    this.initComponent();
  }

  private initComponent = async (): Promise<void> => {
    await this.loadContractors(this.state.selectedCustomerId);

    this.loadVehicles(
      this.state.selectedCustomerId,
      this.state.selectedContractorId
    );
  };

  private loadVehicles = async (
    customerId: number,
    contractorId: number,
    ignoreCache = false
  ): Promise<void> => {
    await this.props.fetchVehicles(ignoreCache);
    const vehicles = filterVehicles(this.props.vehicles.vehiclesList, {
      customerId,
      contractorId,
    });
    this.setState({ ...this.state, vehicles });
  };

  private loadContractors = async (
    customerId: number,
    ignoreCache = false
  ): Promise<void> => {
    if (
      !isNil(this.props.authentication.contractorId) &&
      !hasRole(this.props.authentication.auth0Roles, [
        UserRoles.SUPERADMINISTRATOR,
      ])
    )
      return new Promise((resolve) => resolve(null));
    const contractorsReq = await this.props.getContractors(
      customerId,
      ignoreCache
    );
    const contractors: Array<IContractor> = contractorsReq.contractors
      ? contractorsReq.contractors.sort((a: IContractor, b: IContractor) =>
          a.contractorName.localeCompare(b.contractorName)
        )
      : [];
    const selectedContractorId =
      (Array.isArray(contractors) && contractors[0]?.contractorId) || -1;

    await new Promise((resolve) => {
      this.setState(
        {
          ...this.state,
          contractorsList: contractors,
          selectedContractorId,
        },
        () => resolve(null)
      );
    });
  };

  private handleContractorChanged = (
    selectedContractorId: Record<string, any>
  ) => {
    this.setState({
      ...this.state,
      selectedContractorId: selectedContractorId.value,
    });
    this.loadVehicles(
      this.state.selectedCustomerId,
      selectedContractorId?.value
    );
  };

  private hideVehicleEditDialog(rerender = false): void {
    this.setState({
      ...this.state,
      editDialog: { show: false, vehicle: null },
    });
    if (rerender) {
      this.loadVehicles(
        this.state.selectedCustomerId,
        this.state.selectedContractorId,
        true
      );
    }
  }

  private editVehicle(vehicle: IVehicle) {
    this.openVehicleEditDialog(vehicle);
  }

  private openVehicleEditDialog(vehicle: IVehicle) {
    this.setState({ ...this.state, editDialog: { show: true, vehicle } });
  }

  private getDynamicClassOfComponent = (
    component: 'ContractorSelector' | 'DropdownsRow' | 'AddNewButton'
  ): string => {
    const isAdmin = hasRole(this.props.authentication.auth0Roles, [
      UserRoles.SUPERADMINISTRATOR,
    ]);
    const noContractorId = isNil(this.props.authentication.contractorId);
    const isEditEnabled = this.state.isEditEnabled;

    switch (component) {
      case 'ContractorSelector':
        if (isAdmin) {
          return 'col-sm-6';
        }
        return noContractorId ? (isAdmin ? 'col-sm-6' : 'col-sm-12') : 'd-none';

      case 'DropdownsRow':
        return 'col-md-12';

      case 'AddNewButton':
        return isEditEnabled ? '' : 'd-none';

      default:
        return '';
    }
  };

  public render(): ReactNode {
    return (
      <div className="vehicles">
        <div className="row">
          <PaneHeadline
            titleText={locale.general._vehicles}
            titleIcon={faTruck}
            className="col-sm-6"
          />
          <div className="col-sm-6 text-right">
            {this.state.isEditEnabled && (
              <div className={this.getDynamicClassOfComponent('AddNewButton')}>
                <button
                  className="btn btn-outline-dark"
                  onClick={() => this.openVehicleEditDialog(null)}
                >
                  {locale.vehicles._addNewVehicle}
                </button>
              </div>
            )}
          </div>
        </div>
        <>
          <div className="row">
            <div className={this.getDynamicClassOfComponent('DropdownsRow')}>
              <div className="row">
                <div
                  className={this.getDynamicClassOfComponent(
                    'ContractorSelector'
                  )}
                >
                  {(hasRole(this.props.authentication.auth0Roles, [
                    UserRoles.SUPERADMINISTRATOR,
                  ]) ||
                    isNil(this.props.authentication.contractorId)) && (
                    <ContractorSelector
                      contractors={this.state.contractorsList}
                      handleChange={this.handleContractorChanged}
                      selectedContractorId={this.state.selectedContractorId}
                      className="mb-3"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex">
            <Spinner2 show={this.props.isLoading} />
            <VehiclesGrid
              data={this.state.vehicles}
              isEditEnabled={this.state.isEditEnabled}
              editVehicle={this.editVehicle.bind(this)}
              className="mb-3"
            />
          </div>
        </>
        {this.state.editDialog.show && (
          <VehicleEditDialog
            customerId={this.state.selectedCustomerId}
            contractorId={this.state.selectedContractorId}
            show={this.state.editDialog.show}
            vehicle={this.state.editDialog.vehicle}
            vehicleIdsExisting={this.state.vehicles.map(
              (v: IVehicle) => v.vehicleId?.toLocaleLowerCase() || ''
            )}
            onHide={this.hideVehicleEditDialog.bind(this)}
          />
        )}
      </div>
    );
  }
}

export default Vehicles;
